/**
 * CastLagStore — lightweight module-singleton for looking up the most
 * recent per-cast timing breakdowns by abilityId or globally.
 *
 * Populated by DamagePresentationService when it renders an FCT
 * (so we know which cast the floating number belongs to).
 * Consumed by the in-game right-click attribution tooltip.
 */
import type { CastTimingBreakdown } from '../ui/hooks/useCastLifecycleStream';

interface LagEntry {
  abilityId: string;
  abilityName: string;
  breakdown: CastTimingBreakdown;
  renderedAt: number;
  x: number;
  y: number;
}

const MAX_ENTRIES = 16;
const ENTRIES: LagEntry[] = [];

export const CastLagStore = {
  push(entry: LagEntry): void {
    ENTRIES.push(entry);
    if (ENTRIES.length > MAX_ENTRIES) ENTRIES.shift();
  },

  latest(): LagEntry | undefined {
    return ENTRIES.length > 0 ? ENTRIES[ENTRIES.length - 1] : undefined;
  },

  findNear(x: number, y: number, radiusPx: number = 80): LagEntry | undefined {
    const now = Date.now();
    // Search newest first
    for (let i = ENTRIES.length - 1; i >= 0; i--) {
      const e = ENTRIES[i];
      if (now - e.renderedAt > 3000) continue; // too old
      const dx = e.x - x;
      const dy = e.y - y;
      if (dx * dx + dy * dy <= radiusPx * radiusPx) {
        return e;
      }
    }
    return undefined;
  },

  all(): readonly LagEntry[] {
    return ENTRIES;
  },

  clear(): void {
    ENTRIES.length = 0;
  },
};
